/**
 * <order-card> — the orders destination's entire vocabulary at a glance: who,
 * whether their email reached them, how many tickets, how much, when.
 * Silent on the unremarkable "paid" default — a badge would spend the same
 * visual weight on every row regardless of whether it needs a second look,
 * so status renders as plain typography instead, and color is spent only
 * where an order genuinely does need attention.
 *
 * Delivery rides immediately after the buyer's email, messenger-style: the
 * tick (order-detail.engine.ts's own `deliveryTick`) and its one word travel
 * together, so the state reads where the address it describes is rather than
 * at the far edge of the card. The email truncates rather than push them off.
 * Tone is the tick's alone — primary ink spread over the word too would paint
 * the majority of rows in the screen's own CTA color — and the word is plain
 * rendered text, which a screen reader already reads, so neither an `aria-label`
 * name nor a tooltip is owed. A hard bounce is the one state that breaks that
 * quiet: email, tick, and word all turn error tone and the card itself carries
 * the `error` art overlay — charged edges, drifting static, and lightning
 * striking across it — so a dead delivery is visible from across the list
 * without reading a single row.
 *
 * `cardAccent` picks among the card's four mutually exclusive visual
 * treatments via `choose()`. A bounce gets the `error` overlay, a guest order
 * gets `gift`, and a refunded or ordinary paid order gets none. Precedence
 * runs bounce, then refunded, then guest: bounce first because it is the only
 * one still actionable; refunded over guest because it is a terminal fact
 * about what the row now is — a comp that was reversed is no longer a gift,
 * and champagne gold would assert the opposite of the truth; guest last
 * because it is an origin fact carrying no action. An overlay is applied by
 * swapping the directive for `nothing` rather than by passing it an inert
 * flag, since `art` has no off switch and lit tears a directive down as soon
 * as its value stops being one, so an unaccented card runs no animation loop
 * at all. What does run is silent, GPU-composited, and paused by the
 * directive's own IntersectionObserver while scrolled out of view — a list is
 * somewhere an operator sits, and neither the alarm nor the gift can be one
 * they can't sit next to. The charged edges, drifting static, and lightning
 * of the alarm grammar stay reserved for the bounce; nothing else borrows
 * them.
 *
 * A guest order (row.isGuest — order.origin === 'guest', a comp the console
 * issued rather than a sale) is champagne gold twice over, and the two say
 * different things. The static 4px left-edge border is the index: it lands at
 * the same x on every card, so guest rows read as a break in the left
 * gutter's pattern under a scroll, and it holds while the overlay is
 * IntersectionObserver-paused. The `gift` overlay is the character: a warmth
 * bleeding inward off that same edge and one raking highlight that crosses in
 * under three seconds and then rests for the remaining minute — light moving
 * over foil, which is what a wrapped thing is made of. It paints beneath the
 * card's own text, and at a row's height it stretches its own cycle and
 * offsets its phase against its neighbours, so ten guest rows never sweep at
 * once. Bounce wins when both are true — `cardAccent` resolves bounce before
 * guest. The meta line's trailing "· Guest ticket" is the accessible
 * correlate neither can be — the same word order-detail.view.ts's closing
 * note already uses. No theme token names this accent, so the literal is
 * spelled again on the class below rather than read off the constant —
 * Tailwind's class scanner only picks up a literal string in source, never
 * one built from a JS reference.
 *
 * A refunded order is cool steel blue on three static surfaces, and static is
 * the choice rather than the fallback. The row is 1152×104 — about 11:1 — and
 * every art effect that draws shapes into a normalized square stretches into
 * long vertical rules at that ratio, a spreadsheet row rather than art; what
 * survives 11:1 is the edge-and-wash grammar `error` and `gift` already own,
 * and what a refund means is carried better by type than by motion anyway. So
 * refunded falls through `choose()` to `nothing` and runs no animation loop at
 * all. The 4px left border is the index at a fixed x in the gutter, correct on
 * frame one when a virtualized row remounts mid-scroll. The buyer name takes
 * steel ink — the row's headline, the highest-contrast change available
 * without a fill, and distinct from the guest grammar, which never recolors
 * the name. The amount is struck through in steel: money visibly crossed out
 * says reversed, where an overlay could only say different. The fee suffix
 * stays upright beside it, because an acquiring fee is not returned on a
 * refund (fee.schedule.ts) — a struck amount beside an upright fee is the
 * truthful rendering. There is no background tint: the card is `interactive`,
 * so a faint fill is what its hover state looks like, and a tinted row would
 * read as permanently hovered while hovering it gave no feedback. The
 * warning-tone `statusLabel` is untouched — amber on an otherwise steel card
 * is hierarchy, not collision, and `line-through` is announced by essentially
 * no screen reader, so the word "Refunded" carries the accessible correlate
 * alone.
 */
import { art } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	type OrderRow,
	orderCardRow,
	type Tone,
} from "./order-detail.engine.ts";
import "./order-detail.view.ts";

const TONE_CLASS: Record<Tone, string> = {
	default: "",
	warning: "text-warning-default",
	error: "text-error-default",
};

// A concrete color, not a theme token: `art` writes this into SVG `fill` and
// `stroke` ATTRIBUTES, where a `var(--…)` reference never resolves —
// admin-shell.view.ts passes a resolved color for the same reason. Bright, so
// the effect's own `mix-blend-mode: screen` compositing registers at all.
const BOUNCE_ART_COLOR = "#ef4444";

// Champagne gold — the same literal the guest left border spells, resolved
// here for the same attribute reason as BOUNCE_ART_COLOR above.
const GUEST_ART_COLOR = "#d4af37";

@schmancyElement("order-card")
export class OrderCard extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) order!: OrderRow;

	override render() {
		const row = orderCardRow(this.order);
		const deliveryLabel = this.t(
			`admin.events.console.${row.delivery.labelKey}`,
		);
		const cardAccent = row.delivery.bounced
			? "bounced"
			: this.order.status === "refunded"
				? "refunded"
				: row.isGuest
					? "guest"
					: "none";
		return html`
			<schmancy-card
				${
					choose(
						cardAccent,
						[
							[
								"bounced",
								() => art({ name: "attention", color: BOUNCE_ART_COLOR }),
							],
							["guest", () => art({ name: "gift", color: GUEST_ART_COLOR })],
						],
						() => nothing,
					) ?? nothing
				}
				type="outlined"
				interactive
				class=${this.classMap({
					"w-full rounded-2xl": true,
					// #d4af37 — champagne gold; no theme token names this accent (module doc above).
					"border-l-4 border-l-[#d4af37]": cardAccent === "guest",
					// #7f9cc0 — cool steel blue; same scanner reason as the gold above.
					"border-l-4 border-l-[#7f9cc0]": cardAccent === "refunded",
				})}
				aria-label="${row.buyerName}'s order"
				@schmancy-click=${() => this.openOrder()}
			>
				<div class="flex flex-col gap-2 p-4">
					<div class="flex items-start justify-between gap-3">
						<schmancy-typography type="title" token="md" class=${this.classMap({ "text-[#7f9cc0]": cardAccent === "refunded" })}>${row.buyerName}</schmancy-typography>
						${when(
							row.statusLabel,
							() => html`
								<schmancy-typography type="label" token="sm" class=${TONE_CLASS[row.statusTone]}>
									${row.statusLabel}
								</schmancy-typography>
							`,
						)}
					</div>
					<div class="flex items-center gap-1">
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"min-w-0 flex-1 truncate": true,
								"text-error-default": row.delivery.bounced,
								"text-surface-onVariant": !row.delivery.bounced,
							})}
						>
							${row.buyerEmail}
						</schmancy-typography>
						<span class="flex shrink-0 items-center gap-1">
							<schmancy-icon
								class=${this.classMap({ "text-base": true, [row.delivery.toneClass]: true })}
								>${row.delivery.icon}</schmancy-icon
							>
							<schmancy-typography
								type="label"
								token="sm"
								class=${this.classMap({ "text-error-default": row.delivery.bounced })}
								>${deliveryLabel}</schmancy-typography
							>
						</span>
					</div>
					<!-- The fee is the organiser's own cost, not a fact about the
					     buyer like the rest of this line, so it sits a tone back:
					     two money figures at one weight read as one scan column of
					     equals. -->
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t(row.ticketCount === 1 ? "admin.events.console.orderDetail.ticketCountOne" : "admin.events.console.orderDetail.ticketCountMany", { count: row.ticketCount })} · ${when(
							cardAccent === "refunded",
							() =>
								html`<span class="line-through decoration-2 text-[#7f9cc0]">${row.amountLabel}</span>`,
							() => html`${row.amountLabel}`,
						)}${row.feeLabel === null ? "" : html`<span class="text-surface-onVariant/70">${this.t("admin.events.console.orderDetail.feeSuffix", { fee: row.feeLabel })}</span>`} · ${row.whenLabel}${row.isGuest ? this.t("admin.events.console.orderDetail.guestTicketSuffix") : ""}${when(this.order.payment?.env === "sandbox", () => html` · <span class="text-warning-default">${this.t("admin.events.console.orderDetail.testOrder")}</span>`)}
					</schmancy-typography>
				</div>
			</schmancy-card>
		`;
	}

	private openOrder(): void {
		show(() => html`<order-detail .order=${this.order}></order-detail>`, {
			ariaLabel: `${this.order.buyerName}'s order`,
		})
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-card": OrderCard;
	}
}
